import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { Logger } from 'aws-amplify';
import cleanDeep from 'clean-deep';
import { fabric } from 'fabric';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import { catchError, delay, map, switchMap, tap } from 'rxjs/operators';
import { Doctor, NameFormat, QuestionnaireType, TemperatureUnit } from 'src/API';
import { ClinicService } from 'src/app/core/api/clinic.service';
import { noSort } from 'src/app/core/api/model-utils';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { AssessmentFormService } from 'src/app/encounters/assessment-form.service';
import {
  ContentType,
  ImageToDataUriPipe
} from 'src/app/shared/shared-pipes/image-to-data-uri.pipe';
import { AssessmentBody } from '../../econsult/assessment-body/assessment-body.model';
import { S3AttachmentService, S3Object } from '../attachments/s3-attachment.service';
import { DRIVER_SUB_META } from '../consult-forms/dry-eye-forms/dews-iii/dews-iii-drivers/dews-iii-drivers.component';
import { HomeostasisCategoryKey } from '../consult-forms/dry-eye-forms/dews-iii/dews-iii-homeostasis-markers/dews-iii-homeostasis-markers.component';
import { DEWS_DIFFERENTIAL_DIAGNOSIS_FORM_KEY } from '../consult-forms/dry-eye-forms/dews-iii/dews-iii-medical-history/dews-iii-medical-history.component';
import { DEWS_MEDICAL_HISTORY_FIELDS } from '../consult-forms/dry-eye-forms/dews-iii/dews-iii-medical-history/dews-iii-medical-history.schema';
import {
  NUTRITION_TEST_KEYS,
  NUTRITION_TEST_MODES,
  NUTRITION_TEST_SCHEMA
} from '../consult-forms/dry-eye-forms/nutrition-based-testing/nutrition-based-testing.schema';
import {
  DewsIIIService,
  DiagnosisResult,
  HomeostasisCategory,
  HomeostasisResult
} from '../consult-forms/dry-eye-forms/dews-iii/dews-iii.service';
import { leftEyeKey, rightEyeKey, symptomMethodKey } from '../form.model';
import { DrugFormat } from '../medication-lookup/drug.pipe';
import { SvgPipe } from '../shared-pipes/svg.pipe';
import { SchemaService } from '../symptoms/services/schema/schema.service';
import { SymptomModes } from '../symptoms/symptom.model';
import { ThermalScaleService } from '../symptoms/thermal-scale/thermal-scale.service';
import { ClinicSetupService } from './../../logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { LayoutsService } from './../../logged-in-navbar/clinic-setup-modal/layouts/layouts.service';
import { SymptomTypes } from './../symptoms/symptom.model';
import { defaultLogoUri } from './default-logo-uri.model';
import { UrlToDataUriMap } from './pdf-export-treatment/pdf-export-treatment.component';

const componentStyle = require('./dry-eye-pdf-export.component.css');
const dryEyeCategoryComponentStyle = require('./pdf-export-dry-eye-category/pdf-export-dry-eye-category.component.css');
const googleChartsComponentStyle = require('./pdf-export-donut-chart/pdf-export-donut-chart.component.css');
const pdfSummaryComponentStyle = require('./pdf-summary-export/pdf-summary-export.component.css');
const pdfCustomComponentStyle = require('./pdf-custom-export/pdf-custom-export.component.scss');

export type PDFType = 'simple' | 'detailed' | 'summary' | 'custom';

export type DewsHomeostasisStatusClass =
  | 'dews-result-positive'
  | 'dews-result-negative'
  | 'dews-result-not-done';

export interface DewsHomeostasisRow {
  category: HomeostasisCategoryKey;
  code: string;
  title: string;
  performed: boolean;
  positive: boolean;
  statusLabel: string;
  statusClass: DewsHomeostasisStatusClass;
}

export interface DewsDriverEntry {
  label: string;
  controlKey: string;
}

export interface DewsDriverCategoryRow {
  category: 'tearFilm' | 'eyelidAnomaly' | 'ocularSurface';
  categoryLabel: string;
  drivers: DewsDriverEntry[];
}

export interface DewsMedicalHistoryExtra {
  key: string;
  value: string;
}

export interface NutritionTestRow {
  key: string;
  value: string | number;
  units: string;
  label: string;
  // Passed to csi-pdf-export-thermal-scale's [config] input. These keys are not in
  // schema.json, so the component's own schemaService.getThermalScaleConfig() lookup
  // would resolve null — the config has to come from the local nutrition schema.
  thermalScale: any;
}

@Component({
  selector: 'csi-dry-eye-pdf-export',
  templateUrl: './dry-eye-pdf-export.component.html',
  styleUrls: ['./dry-eye-pdf-export.component.css'],
  providers: [SvgPipe, ImageToDataUriPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DryEyePdfExportComponent implements OnInit, OnDestroy {
  customWrapper!: ElementRef<HTMLElement>;
  public TemperatureUnit = TemperatureUnit;
  private _data: AssessmentBody;
  private _clinicLogo: S3Object;
  public defaultLogo = defaultLogoUri;
  public medicalHistoryShowFields = [];

  clinicLogoDataUri: SafeUrl;
  eyeDropImageDataUris: UrlToDataUriMap = {};

  public readonly SymptomModes = SymptomModes;

  public readonly NameFormat = NameFormat;
  public readonly DrugFormat = DrugFormat;
  public readonly SymptomTypes = SymptomTypes;
  public localMedicalHistoryFields = [];
  public localMedicationFields = [];
  public interpretation: string[] = [];
  public riskFactors: string[] = [];
  public riskFactorTitle: string;
  public htmlClassExtractor: { [key: string]: string } = {
    interpretation: 'interpretation-data-container',
    riskFactor: 'risk-factors-data-container'
  };

  private readonly RemovedTextDecoration = `h4[style*='underline']`;
  public isLoaded = false;
  public loaded = false;

  public elementColumnConfigp = {
    riskFactor: { columns: 1 }
  };
  public elementColumnConfigMap: {
    riskFactor: { [key: string]: number };
  } = { riskFactor: {} };

  private logger: Logger = new Logger('DryEyePdfExportComponent');
  public medHistForm: any;

  public drawnSignatureDataUrl: string | null = null;
  @Input() signingDoctor: Partial<Doctor>;

  @Input('data')
  set data(value: AssessmentBody) {
    this.logger.debug('setting data');
    // remove empty objects so we can use ngIf to test for values
    this._data = cleanDeep(value) as AssessmentBody;

    this.medHistForm = this._data.medicalHistoryForm;
  }

  get data(): AssessmentBody {
    return this._data;
  }

  @Input() translateObject: any;

  @Input('clinicLogo')
  set clinicLogo(value: S3Object) {
    if (this.clinicLogo !== value) {
      this._clinicLogo = value;
      this.renderClinicLogo().subscribe();
    }
  }

  @Input() pdfType: PDFType;
  @Input() hideDataForFaxing = false;
  @Input() previousAssessmentBody: AssessmentBody;

  @Input() lang: string;
  public isAssessmentLocked: boolean;
  public isAtRisk: boolean;

  public isDewsIII = false;
  public dewsOsdi6Score: number = null;
  public dewsHomeostasisResults: HomeostasisResult[] = [];
  public dewsDiagnosis: DiagnosisResult = null;
  public dewsDiagnosisLabel = '';
  public dewsDiagnosisBannerClass = 'banner-unknown';
  public dewsSymptomClassificationLabel = '';
  public dewsDiscordanceGuidance = '';
  public dewsSymptomsPresent = false;
  public dewsClinicianOverride = false;
  public dewsDiagnosisNotes = '';
  public dewsHomeostasisRows: DewsHomeostasisRow[] = [];
  public dewsDriversByCategory: DewsDriverCategoryRow[] = [];
  public dewsHasIdentifiedDrivers = false;
  public dewsMedicalHistoryExtras: DewsMedicalHistoryExtra[] = [];
  public dewsMedicalHistoryFlags: string[] = [];

  public nutritionRows: NutritionTestRow[] = [];
  public showNutritionSection = false;

  private readonly dewsMedicalHistoryExtraKeys = [
    'urgentScreening',
    'painAssessment',
    'systemicConditions',
    'laterality',
    'allergyInfection',
    'medicationsLifestyle'
  ];

  private readonly dewsDriverCategoryLabels: { [key: string]: string } = {
    tearFilm: 'Tear Film Drivers',
    eyelidAnomaly: 'Eyelid Anomaly Drivers',
    ocularSurface: 'Ocular Surface Drivers'
  };

  constructor(
    private hostElementRef: ElementRef,
    public schemaService: SchemaService,
    public layoutsService: LayoutsService,
    private imageToDataUriPipe: ImageToDataUriPipe,
    private s3AttachmentService: S3AttachmentService,
    private httpClient: HttpClient,
    private changeDetector: ChangeDetectorRef,
    private clinicService: ClinicService,
    public clinicSetupService: ClinicSetupService,
    public translocoService: TranslocoService,
    public assessmentFormService: AssessmentFormService,
    public questionnaireRequestService: QuestionnaireRequestService,
    private dewsIIIService: DewsIIIService,
    private thermalScaleService: ThermalScaleService
  ) {}
  ngOnDestroy(): void {}

  ngOnInit() {
    const layout = this.layoutsService.allLayoutsById[this.data.dryEyeForm.assessmentMethod];
    this.isAssessmentLocked = this.assessmentFormService.isAssessmentLocked;

    //Current ocular medications and drops key: currentOcularMedicationsFreeText
    //Past ocular medications and drops key: pastOcularMedicationsFreeText
    //Should always be hidden as all medication are linked to the medication section

    this.localMedicationFields = [
      'patientEnteredMedicationsFreeText',
      'currentOcularMedicationsFreeText',
      'pastOcularMedicationsFreeText',
      'medications'
    ];

    const medicalHistoryShow =
      layout && layout.medicalHistoryLayout && layout.medicalHistoryLayout.show;
    this.localMedicalHistoryFields = (medicalHistoryShow || []).filter(
      field => !this.localMedicationFields.includes(field)
    );

    const scores = JSON.stringify({
      [QuestionnaireType.SPEED]: this.data.dryEyeForm['speed'],
      [QuestionnaireType.SPEEDII]: this.data.dryEyeForm['speedii'],
      [QuestionnaireType.DEQ]: this.data.dryEyeForm['deq'],
      [QuestionnaireType.OSDI]: this.data.dryEyeForm['osdi'],
      [QuestionnaireType.FITZPATRICK]: this.data.dryEyeForm['fitzpatrick'],
      [QuestionnaireType.OSDI6]: this.data.dryEyeForm['osdi6']
    });
    this.isAtRisk = this.questionnaireRequestService.isAtRisk(scores);

    this.isDewsIII = this.data.dryEyeForm.assessmentMethod === SymptomModes.DewsIII;
    if (this.isDewsIII) {
      this.computeDewsIIIDisplayState();
    }

    this.computeNutritionDisplayState();
  }

  // The detailed report is not plan-gated itself (gating happens on the export button via
  // 'reports.<type>'), but a clinic without the feature can never have entered these
  // values, so the data-presence check below is enough on its own.
  private computeNutritionDisplayState(): void {
    const form = this.data.dryEyeForm as any;

    this.nutritionRows = NUTRITION_TEST_KEYS.filter(key => {
      const value = form[key];
      return value !== null && value !== undefined && value !== '';
    }).map(key => {
      const definition = NUTRITION_TEST_SCHEMA[key];
      // Pre-parsed here rather than calling parseValue() from the template, so the
      // binding stays a plain property read.
      const value = this.parseValue(form[key]);
      return {
        key: key,
        value: value,
        units: (definition as any).units || '',
        label: this.thermalScaleService.getLabel(value, definition.thermalScale),
        thermalScale: definition.thermalScale
      };
    });

    const isNutritionMethod =
      NUTRITION_TEST_MODES.indexOf(this.data.dryEyeForm.assessmentMethod as SymptomModes) !== -1;

    this.showNutritionSection =
      this.pdfType === 'detailed' && isNutritionMethod && this.nutritionRows.length > 0;
  }

  private computeDewsIIIDisplayState(): void {
    const form = this.data.dryEyeForm as any;

    const rawOsdi6 = form['osdi6'];
    this.dewsOsdi6Score = rawOsdi6 === null || rawOsdi6 === undefined ? null : rawOsdi6;

    const savedResults = form['dewsHomeostasisResults'];
    this.dewsHomeostasisResults =
      Array.isArray(savedResults) && savedResults.length > 0
        ? savedResults
        : this.defaultHomeostasisResults();

    this.dewsDiagnosis = this.dewsIIIService.determineDiagnosis(
      this.dewsOsdi6Score,
      this.dewsHomeostasisResults
    );
    this.dewsDiagnosisLabel = this.dewsIIIService.diagnosisLabel(
      this.dewsDiagnosis,
      this.dewsOsdi6Score
    );
    this.dewsDiagnosisBannerClass = this.dewsIIIService.diagnosisBannerClass(this.dewsDiagnosis);
    this.dewsSymptomClassificationLabel = this.dewsIIIService.symptomClassificationLabel(
      this.dewsOsdi6Score
    );
    this.dewsDiscordanceGuidance = this.dewsIIIService.discordanceGuidance(this.dewsDiagnosis);
    this.dewsSymptomsPresent = this.dewsIIIService.hasSymptoms(this.dewsOsdi6Score);
    this.dewsClinicianOverride = form['dewsClinicianOverride'] === true;
    this.dewsDiagnosisNotes = form['dewsDiagnosisNotes'] || '';

    this.dewsHomeostasisRows = this.buildHomeostasisRows();
    this.dewsDriversByCategory = this.buildDriversByCategory(form);
    this.dewsHasIdentifiedDrivers = this.dewsDriversByCategory.some(row => row.drivers.length > 0);
    this.dewsMedicalHistoryExtras = this.buildMedicalHistoryExtras(form);
    this.dewsMedicalHistoryFlags = this.buildMedicalHistoryFlags(form);
  }

  private defaultHomeostasisResults(): HomeostasisResult[] {
    const categories: HomeostasisCategory[] = [
      'tearFilmStability',
      'osmolarity',
      'ocularSurfaceStaining'
    ];
    return categories.map(category => ({
      category,
      performed: false,
      positive: false
    }));
  }

  private buildHomeostasisRows(): DewsHomeostasisRow[] {
    const categoryKeys: HomeostasisCategoryKey[] = [
      'tearFilmStability',
      'osmolarity',
      'ocularSurfaceStaining'
    ];
    const labels: { [key: string]: { code: string; title: string } } = {
      tearFilmStability: { code: '3A', title: 'Tear Film Stability' },
      osmolarity: { code: '3B', title: 'Tear Osmolarity' },
      ocularSurfaceStaining: { code: '3C', title: 'Ocular Surface Staining' }
    };

    return categoryKeys.map(category => {
      const result = this.dewsHomeostasisResults.find(r => r.category === category);
      const performed = !!(result && result.performed);
      const positive = !!(result && result.positive);
      let statusLabel: string;
      let statusClass: DewsHomeostasisStatusClass;
      if (!performed) {
        statusLabel = 'Not Done';
        statusClass = 'dews-result-not-done';
      } else if (positive) {
        statusLabel = 'Positive';
        statusClass = 'dews-result-positive';
      } else {
        statusLabel = 'Negative';
        statusClass = 'dews-result-negative';
      }
      return {
        category,
        code: labels[category].code,
        title: labels[category].title,
        performed,
        positive,
        statusLabel,
        statusClass
      };
    });
  }

  private buildDriversByCategory(form: any): DewsDriverCategoryRow[] {
    const order: Array<'tearFilm' | 'eyelidAnomaly' | 'ocularSurface'> = [
      'tearFilm',
      'eyelidAnomaly',
      'ocularSurface'
    ];

    return order.map(category => {
      const drivers = DRIVER_SUB_META.filter(
        meta => meta.category === category && form[meta.controlKey] === true
      ).map(meta => ({ label: meta.label, controlKey: meta.controlKey }));
      return {
        category,
        categoryLabel: this.dewsDriverCategoryLabels[category],
        drivers
      };
    });
  }

  private buildMedicalHistoryFlags(form: any): string[] {
    const nestedGroup = form[DEWS_DIFFERENTIAL_DIAGNOSIS_FORM_KEY];
    if (!nestedGroup) {
      return [];
    }
    return DEWS_MEDICAL_HISTORY_FIELDS.filter(f => nestedGroup[f.key] === f.expected).map(
      f => f.flagLabel
    );
  }

  private buildMedicalHistoryExtras(form: any): DewsMedicalHistoryExtra[] {
    return this.dewsMedicalHistoryExtraKeys
      .map(key => {
        const raw = form[key];
        const value = this.formatMedicalHistoryValue(raw);
        return value ? { key, value } : null;
      })
      .filter(entry => entry !== null);
  }

  private formatMedicalHistoryValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      const cleaned = value.filter(v => v !== null && v !== undefined && v !== '');
      return cleaned.length > 0 ? cleaned.join(', ') : '';
    }
    if (typeof value === 'object') {
      const entries = Object.entries(value)
        .filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== false)
        .map(([k, v]) => {
          const label = k[0].toUpperCase() + k.substring(1);
          return v === true ? label : `${label}: ${v}`;
        });
      return entries.length > 0 ? entries.join(', ') : '';
    }
    return String(value);
  }

  ngAfterViewInit() {
    if (this.pdfType === 'summary' || this.pdfType === 'custom') {
      if (this._data.assessmentForm !== undefined) {
        // this.extractHtmlDom(this._data.assessmentForm.impressions, true);
      }
    }
  }

  parseValue(val: any) {
    if (typeof val === 'object') {
      return Object.entries(val)
        .map(([k, v]) => `${k[0].toUpperCase() + k.substring(1)}: ${v}`)
        .join(', ');
    }
    return val;
  }

  get clinicLogo(): S3Object {
    return this._clinicLogo;
  }

  get html(): Observable<string> {
    const layout = this.layoutsService.allLayoutsById[this.data.dryEyeForm.assessmentMethod];

    this.medicalHistoryShowFields = (layout && layout.medicalHistoryLayout.show) || [];

    // Detect changes here to avoid empty data due to race conditions
    this.changeDetector.detectChanges();

    this.logger.debug('rendering pdf html');
    return this.render().pipe(
      delay(100),
      tap(() => {
        this.changeDetector.detectChanges();
      }),
      map(() => {
        this.changeDetector.detectChanges();
        this.logger.debug('calling innerHTMLWithStyle');
        const html = this.innerHTMLWithStyle;
        return html;
      }),
      catchError((err, caught) => {
        console.log(err, caught);
        return EMPTY;
      })
    );
  }

  private render(): Observable<boolean> {
    this.logger.debug('render() called');
    const renderClinicLogo$: Observable<boolean> = this.clinicLogoDataUri
      ? of(true)
      : this.renderClinicLogo();

    let allEyeDrops = null;
    if (this.data && this.data.assessmentForm) {
      allEyeDrops = [
        ...(this.data.assessmentForm.recommendedEyeDrops || []),
        ...(this.data.assessmentForm.recommendedRxEyeDrops || []),
        ...(this.data.assessmentForm.recommendedExercises || []),
        ...(this.data.assessmentForm.recommendedProcedures || [])
      ].filter(eyeDrop => !!eyeDrop);
    }

    const renderEyeDropImages$: Observable<boolean> = of(allEyeDrops).pipe(
      switchMap(eyeDrops => {
        const dataUris$: Observable<SafeUrl>[] = [];
        if (eyeDrops && eyeDrops.length > 0) {
          eyeDrops.forEach(eyeDrop => {
            if (!!eyeDrop.image) {
              dataUris$.push(
                this.getImageDataUri(eyeDrop.image).pipe(
                  tap(safeUrl => {
                    this.logger.debug(`setting safeUrl for ${eyeDrop.image}`);
                    this.eyeDropImageDataUris[eyeDrop.image] = safeUrl;
                  })
                )
              );
            }
          });
        }
        return dataUris$.length > 0
          ? forkJoin([...dataUris$]).pipe(
              map(() => {
                this.logger.debug('got all safe urls');
                return true;
              })
            )
          : of(true);
      })
    );

    const renderDrawnSignature$: Observable<boolean> = of(this.data).pipe(
      switchMap(data => {
        const signOff = data.interactionSignoffGroup;
        if (signOff && signOff['signatureType'] === 'drawn' && signOff['drawnSignature']) {
          return this.convertSignatureToDataUrl(signOff['drawnSignature']).pipe(
            tap(dataUrl => {
              this.drawnSignatureDataUrl = dataUrl;
              this.changeDetector.detectChanges();
            }),
            map(() => true),
            catchError(error => {
              console.error('Signature render error:', error);
              return of(true);
            })
          );
        }
        return of(true);
      })
    );

    this.logger.debug('returning fork join');

    return forkJoin([renderClinicLogo$, renderEyeDropImages$, renderDrawnSignature$]).pipe(
      tap(() => this.changeDetector.detectChanges()),
      map(() => true)
    );
  }

  private convertSignatureToDataUrl(signatureJson: any): Observable<string> {
    return new Observable(observer => {
      let jsonObj = signatureJson;
      if (typeof jsonObj === 'string') {
        try {
          jsonObj = JSON.parse(jsonObj);
        } catch {
          return observer.error(new Error('Malformed JSON'));
        }
      }
      if (!Array.isArray(jsonObj.objects)) {
        return observer.error(new Error('Missing objects[] in JSON'));
      }

      const canvas = new fabric.Canvas(document.createElement('canvas'), {
        width: 550,
        height: 150,
        backgroundColor: '#fff',
        enableRetinaScaling: false
      });

      try {
        canvas.loadFromJSON(jsonObj, () => {
          canvas.getObjects().forEach(obj => {
            obj.selectable = obj.evented = false;
            if (obj.type === 'path' && !obj.stroke) {
              obj.set('stroke', '#000');
            }
          });

          canvas.renderAll();
          const dataUrl = canvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });
          observer.next(dataUrl);
          observer.complete();
        });
      } catch (e) {
        observer.error(new Error(`Fabric load failed: ${e.message}`));
      }
    });
  }

  private renderClinicLogo(): Observable<boolean> {
    this.logger.debug('rendering clinic logo');
    return of(this.clinicLogo).pipe(
      switchMap(logo => (logo ? this.s3AttachmentService.s3ObjectToUri(logo) : of(null))),
      map(logoDataUri => {
        this.logger.debug('setting logo data uri');
        this.clinicLogoDataUri = logoDataUri;

        return true;
      })
    );
  }

  private getImageDataUri(url: string): Observable<SafeUrl> {
    this.logger.debug(`getImageDataUri: getting ${url}`);
    return this.httpClient.get(url, { responseType: 'blob' }).pipe(
      switchMap(imageBlob => {
        this.logger.debug('got image blob');
        return this.imageToDataUriPipe.transform(imageBlob, imageBlob.type as ContentType, true);
      })
    );
  }

  private get innerHTMLWithStyle(): string {
    this.logger.debug('setting inner html');
    return this.hostElement.innerHTML.replace(
      '</head>',
      `<style>${componentStyle.default}
       ${dryEyeCategoryComponentStyle.default}
       ${googleChartsComponentStyle.default}
        ${pdfSummaryComponentStyle.default}
         ${pdfCustomComponentStyle.default}</style></head>`
    );
  }

  public hasValue(value: string): boolean {
    if (value === null || value === undefined) {
      return false;
    } else {
      return value.toString().length > 0;
    }
  }

  get assessmentDate(): Date {
    // TODO: Support custom assessment date
    return new Date();
  }

  public shouldShowSymptom(symptomKey: string): boolean {
    return (
      (this.isSimpleMode && this.isSimpleSymptom(symptomKey)) ||
      (this.isAdvancedMode && this.isAdvancedSymptom(symptomKey))
    );
  }

  public shouldShowSymptomMethod(symptomKey: string): { right: boolean; left: boolean } {
    return {
      right:
        this.schemaService.symptomMap[symptomKey].type === SymptomTypes.Method &&
        this.hasValue(rightEyeKey(symptomMethodKey(symptomKey))) &&
        this.hasValue(rightEyeKey(symptomKey)),

      left:
        this.schemaService.symptomMap[symptomKey].type === SymptomTypes.Method &&
        this.hasValue(leftEyeKey(symptomMethodKey(symptomKey))) &&
        this.hasValue(leftEyeKey(symptomKey))
    };
  }

  public isSimpleSymptom(symptomKey: string): boolean {
    return this.schemaService.symptomMap[symptomKey].modes.indexOf(SymptomModes.Simple) !== -1;
  }

  public isAdvancedSymptom(symptomKey: string): boolean {
    return this.schemaService.symptomMap[symptomKey].modes.indexOf(SymptomModes.Advanced) !== -1;
  }

  get isSimpleMode(): boolean {
    return this.data.dryEyeForm.assessmentMethod === SymptomModes.Simple;
  }

  get isAdvancedMode(): boolean {
    return this.data.dryEyeForm.assessmentMethod === SymptomModes.Advanced;
  }

  private get hostElement(): HTMLElement {
    return this.hostElementRef.nativeElement;
  }
  public noSort = noSort;

  public extractHtmlDom(htmlBody: string, removeFromHtml = false) {
    if (!htmlBody) {
      return;
    }
    const parser = new DOMParser();
    const dom = parser.parseFromString(htmlBody, 'text/html');

    const interpEl = Array.from(dom.querySelectorAll('.interpretation-data-container'));
    console.log(interpEl);

    if (interpEl.length > 0) {
      interpEl.forEach(interElement => {
        console.log(interElement);
        const title = interElement.querySelector('interpretation-title');
        if (title) title.remove();
        this.interpretation.length > 0
          ? this.interpretation.push(
              `<div class='summary-interpretation-container'>${interElement.innerHTML}</div>`
            )
          : this.interpretation.push(interElement.innerHTML);
        interElement.remove();
      });
    }

    const riskElList = dom.querySelectorAll('.risk-factors-data-container');

    if (riskElList.length > 0) {
      riskElList.forEach((riskElement, index) => {
        const titleEl = riskElement.querySelector('.risk-factor-title');
        this.riskFactorTitle = titleEl ? titleEl.outerHTML : '';
        if (titleEl) titleEl.remove();
        this.riskFactors.length > 0
          ? this.riskFactors.push(
              `<div class='summary-interpretation-container'>${riskElement.innerHTML}</div>`
            )
          : this.riskFactors.push(riskElement.innerHTML);
        console.log(riskElement);
        this.riskFactorColumns(riskElement.innerHTML, index);
        riskElement.remove();
      });
    }
    this._data.assessmentForm.impressions = new XMLSerializer().serializeToString(dom);
  }

  private riskFactorColumns(riskFactorElement: string, index: number) {
    this.elementColumnConfigMap.riskFactor[index] =
      (riskFactorElement.match(/<\/li>/gi) || []).length >= 10 ? 2 : 1;
  }
}
